import React, { useState, useEffect, useRef } from "react";
import { colors, fonts } from "../utils/constants.js";
import { isMobile, getButtonStyle } from "../utils/responsive.js";

function EnhancedMarketModal({ 
  market, 
  onClose, 
  onVote, 
  userVote = null,
  isGuest = false,
  user = null 
}) {
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [containerWidth, setContainerWidth] = useState(560);
  const chartContainer = useRef(null);

  useEffect(() => {
    const checkIsMobile = () => setIsMobileDevice(isMobile());
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  // Chart responsive sizing
  useEffect(() => {
    if (chartContainer.current) {
      const updateWidth = () => {
        const width = Math.min(chartContainer.current.offsetWidth - 32, 560);
        setContainerWidth(width);
      };
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, [chartContainer]);

  if (!market) return null;

  const marketType = market.market_type || 'binary';
  const isResolved = market.status === "final";
  const hasVoted = !!userVote;
  const options = market.options || [];

  const getCategoryIcon = (category) => {
    const icons = {
      politics: "🏛️",
      economy: "💰", 
      sports: "⚽",
      culture: "🎭",
      technology: "💻",
      demographics: "👥",
      environment: "🌱",
      international: "🌍",
      entertainment: "🎬",
      science: "🔬"
    };
    return icons[category] || "📊";
  };

  const handleVote = async (voteData) => {
    if (hasVoted || isResolved || isVoting) return;
    
    setIsVoting(true);
    try {
      await onVote({ ...voteData, market_id: market.id });
    } catch (error) {
      console.error('Vote error:', error);
    } finally {
      setIsVoting(false);
      setSelectedOption(null);
    }
  };

  const renderBinaryChart = () => {
    const yesPercentage = Math.round(market.yes_probability * 100);
    const noPercentage = 100 - yesPercentage;
    const chartHeight = 120;
    
    return (
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        {/* Probability bars */}
        <div style={{ 
          display: "flex", 
          height: 12, 
          borderRadius: 6,
          overflow: "hidden",
          marginBottom: 16,
          background: colors.border
        }}>
          <div 
            style={{ 
              width: `${yesPercentage}%`,
              background: colors.green,
              transition: "width 0.5s ease"
            }} 
          />
          <div 
            style={{ 
              width: `${noPercentage}%`,
              background: colors.red,
              transition: "width 0.5s ease"
            }} 
          />
        </div>

        {/* Percentage display */}
        <div style={{ display: "flex", justifyContent: "center", gap: 40 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ 
              fontSize: 36, 
              fontWeight: 700, 
              color: colors.green,
              marginBottom: 4
            }}>
              {yesPercentage}%
            </div>
            <div style={{ 
              fontSize: 14, 
              fontWeight: 600, 
              color: colors.green,
              letterSpacing: "0.05em"
            }}>
              ДА
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ 
              fontSize: 36, 
              fontWeight: 700, 
              color: colors.red,
              marginBottom: 4
            }}>
              {noPercentage}%
            </div>
            <div style={{ 
              fontSize: 14, 
              fontWeight: 600, 
              color: colors.red,
              letterSpacing: "0.05em"
            }}>
              НЕ
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCategoricalChart = () => {
    const sortedOptions = [...options].sort((a, b) => (b.probability || 0) - (a.probability || 0));
    
    return (
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ 
          margin: "0 0 16px 0", 
          fontSize: 16, 
          fontWeight: 600, 
          color: colors.text 
        }}>
          Текуща класация:
        </h4>
        
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sortedOptions.map((option, index) => {
            const percentage = Math.round((option.probability || 0) * 100);
            const isSelected = selectedOption === option.id;
            const isUserVote = hasVoted && userVote?.option_id === option.id;
            
            return (
              <div
                key={option.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: `1px solid ${isUserVote ? colors.green : colors.border}`,
                  background: isUserVote ? colors.greenBg : 
                             isSelected ? colors.blueBg : colors.card,
                  cursor: hasVoted || isResolved ? "default" : "pointer",
                  transition: "all 0.2s",
                }}
                onClick={() => !hasVoted && !isResolved && setSelectedOption(option.id)}
              >
                {/* Rank */}
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: index === 0 ? colors.green : colors.textTer,
                  color: "#FFF",
                  fontSize: 12,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {index + 1}
                </div>

                {/* Option text */}
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: colors.text,
                    marginBottom: 2
                  }}>
                    {option.option_text}
                  </div>
                  {option.option_text_en && (
                    <div style={{ 
                      fontSize: 12, 
                      color: colors.textSec 
                    }}>
                      {option.option_text_en}
                    </div>
                  )}
                </div>

                {/* Percentage and bar */}
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 12,
                  minWidth: 80
                }}>
                  <div style={{ 
                    fontSize: 18, 
                    fontWeight: 700, 
                    color: colors.text 
                  }}>
                    {percentage}%
                  </div>
                  <div style={{
                    width: 40,
                    height: 4,
                    background: colors.border,
                    borderRadius: 2,
                    overflow: "hidden"
                  }}>
                    <div style={{
                      width: `${percentage}%`,
                      height: "100%",
                      background: isUserVote ? colors.green : colors.blue,
                      transition: "width 0.3s ease"
                    }} />
                  </div>
                </div>

                {/* User vote indicator */}
                {isUserVote && (
                  <div style={{
                    color: colors.green,
                    fontSize: 16,
                    marginLeft: 8
                  }}>
                    ✓
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderRangeChart = () => {
    const sortedOptions = [...options].sort((a, b) => a.option_order - b.option_order);
    
    return (
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ 
          margin: "0 0 16px 0", 
          fontSize: 16, 
          fontWeight: 600, 
          color: colors.text 
        }}>
          Разпределение по диапазони:
        </h4>
        
        {/* Range visualization */}
        <div style={{
          display: "flex",
          height: 60,
          borderRadius: 8,
          overflow: "hidden",
          border: `1px solid ${colors.border}`,
          marginBottom: 16
        }}>
          {sortedOptions.map((option, index) => {
            const percentage = Math.round((option.probability || 0) * 100);
            const isUserVote = hasVoted && userVote?.option_id === option.id;
            
            return (
              <div
                key={option.id}
                style={{
                  flex: Math.max(option.probability || 0.1, 0.1),
                  background: isUserVote ? colors.green :
                             `hsl(${200 + (index * 40)}, 70%, ${80 - percentage * 0.4}%)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: hasVoted || isResolved ? "default" : "pointer",
                  transition: "all 0.3s",
                  position: "relative",
                }}
                onClick={() => !hasVoted && !isResolved && setSelectedOption(option.id)}
                title={`${option.option_text}: ${percentage}%`}
              >
                <div style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: isUserVote ? "#FFF" : colors.text,
                  marginBottom: 2
                }}>
                  {percentage}%
                </div>
                {isUserVote && (
                  <div style={{
                    color: "#FFF",
                    fontSize: 12
                  }}>
                    ✓
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Range labels */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between",
          fontSize: 12,
          color: colors.textSec,
        }}>
          {sortedOptions.map((option) => (
            <span 
              key={option.id}
              style={{ 
                textAlign: "center",
                flex: Math.max(option.probability || 0.1, 0.1),
                padding: "0 4px"
              }}
            >
              {option.option_text}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderVotingInterface = () => {
    if (hasVoted || isResolved) return null;

    switch (marketType) {
      case 'binary':
        return (
          <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
            <button
              onClick={() => handleVote({ side: "YES" })}
              disabled={isVoting}
              style={{
                ...getButtonStyle("primary", "medium", isMobileDevice),
                flex: 1,
                background: colors.green,
                color: "#FFF",
                border: "none",
                opacity: isVoting ? 0.7 : 1,
              }}
            >
              {isVoting ? "Гласуване..." : "Гласувай ДА"}
            </button>
            <button
              onClick={() => handleVote({ side: "NO" })}
              disabled={isVoting}
              style={{
                ...getButtonStyle("primary", "medium", isMobileDevice),
                flex: 1,
                background: colors.red,
                color: "#FFF",
                border: "none",
                opacity: isVoting ? 0.7 : 1,
              }}
            >
              {isVoting ? "Гласуване..." : "Гласувай НЕ"}
            </button>
          </div>
        );

      case 'categorical':
      case 'range':
        return (
          <div style={{ marginBottom: 20 }}>
            {selectedOption ? (
              <div style={{
                padding: 16,
                background: colors.blueBg,
                borderRadius: 8,
                border: `1px solid ${colors.blueBorder}`,
                marginBottom: 12
              }}>
                <div style={{ fontSize: 14, color: colors.blue, marginBottom: 8 }}>
                  Избрана опция:
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 12 }}>
                  {options.find(opt => opt.id === selectedOption)?.option_text}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => handleVote({ option_id: selectedOption })}
                    disabled={isVoting}
                    style={{
                      ...getButtonStyle("primary", "medium", isMobileDevice),
                      background: colors.green,
                      color: "#FFF",
                      border: "none",
                      opacity: isVoting ? 0.7 : 1,
                    }}
                  >
                    {isVoting ? "Гласуване..." : "Потвърди гласа"}
                  </button>
                  <button
                    onClick={() => setSelectedOption(null)}
                    disabled={isVoting}
                    style={{
                      ...getButtonStyle("secondary", "medium", isMobileDevice),
                      background: "transparent",
                      color: colors.textSec,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    Отказ
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ 
                fontSize: 14, 
                color: colors.textSec, 
                textAlign: "center",
                padding: 16,
                background: colors.hover,
                borderRadius: 8
              }}>
                Изберете опция {marketType === 'categorical' ? 'от списъка' : 'от диапазона'} по-горе за да гласувате
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const daysLeft = Math.max(0, Math.ceil((new Date(market.end_date) - new Date()) / 86400000));

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        ref={chartContainer}
        style={{
          background: colors.card,
          borderRadius: 12,
          padding: isMobileDevice ? 20 : 24,
          maxWidth: "min(640px, 100vw - 32px)",
          width: "100%",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            background: "transparent",
            border: "none",
            fontSize: 24,
            cursor: "pointer",
            color: colors.textTer,
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 4,
            zIndex: 1,
          }}
          onMouseEnter={(e) => {
            e.target.style.background = colors.hover;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "transparent";
          }}
        >
          ×
        </button>

        {/* Header */}
        <div style={{ marginBottom: 20, paddingRight: 40 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            {/* Category */}
            <span style={{ fontSize: 20 }}>{getCategoryIcon(market.category)}</span>
            <span style={{ 
              fontSize: 12, 
              fontWeight: 600, 
              color: colors.textSec,
              textTransform: "uppercase",
              letterSpacing: "0.05em"
            }}>
              {market.category}
            </span>

            {/* Market type */}
            {marketType !== 'binary' && (
              <span style={{ 
                padding: "2px 6px",
                borderRadius: 3,
                background: colors.blueBg,
                color: colors.blue,
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase"
              }}>
                {marketType === 'categorical' ? 'Много избора' : 'Диапазон'}
              </span>
            )}
          </div>

          <h2 style={{
            margin: 0,
            fontSize: isMobileDevice ? 20 : 24,
            fontWeight: 700,
            lineHeight: 1.3,
            color: colors.text,
          }}>
            {market.question}
          </h2>
        </div>

        {/* Chart/Visualization */}
        {marketType === 'binary' && renderBinaryChart()}
        {marketType === 'categorical' && renderCategoricalChart()}
        {marketType === 'range' && renderRangeChart()}

        {/* Voting interface */}
        {renderVotingInterface()}

        {/* Description */}
        {market.description && (
          <div style={{
            padding: 16,
            background: colors.hover,
            borderRadius: 8,
            marginBottom: 16,
          }}>
            <p style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.5,
              color: colors.textSec,
            }}>
              {market.description}
            </p>
          </div>
        )}

        {/* Footer */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          paddingTop: 16,
          borderTop: `1px solid ${colors.border}`,
          fontSize: 13,
          color: colors.textSec,
        }}>
          <div>
            {market.volume} гласа всичко
            {isResolved ? (
              <span style={{ color: colors.green, fontWeight: 600, marginLeft: 8 }}>
                РЕШЕНА
              </span>
            ) : (
              <span> • {daysLeft} дни остават</span>
            )}
          </div>

          {hasVoted && (
            <span style={{
              color: colors.green,
              fontSize: 12,
              fontWeight: 600,
            }}>
              ✓ Вие гласувахте
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default EnhancedMarketModal;